import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  UserPlus, 
  Trash2, 
  Users,
  Mail,
  Phone,
  User,
  Plus,
  X,
  Clock,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Gift,
  MessageSquare,
  AlertTriangle,
  CreditCard,
  Package,
  MapPin,
  Truck,
  ShoppingCart
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency, type Currency } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { ShippingAddressForm } from "@/components/ShippingAddressForm";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { useCartStore } from "@/stores/cartStore";
import { RecurringReminderDialog } from "@/components/RecurringReminderDialog";

interface Participant {
  id: string;
  name: string;
  mobile: string;
  emails: string[];
}

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  zip: string;
  phone: string;
}

interface PendingRequest {
  id: string;
  session_token: string;
  merchant_id: string;
  shopify_draft_order_id: string;
  variant_id: string;
  product_title: string;
  product_image: string | null;
  quantity: number;
  status: string;
  subtotal: number;
  total_tax: number;
  total_shipping: number;
  total_price: number;
  customer_email: string | null;
  shipping_address: any;
  participant_count: number | null;
}

interface MerchantSettings {
  store_name: string;
  store_domain: string;
  default_deadline_hours: number;
  cancellation_fee_type: string;
  cancellation_fee_value: number;
  currency: Currency;
}

// NOTE: Using hoisted function declarations to prevent TDZ errors in production builds
function getDeadlineLabel(hours: number): string {
  if (hours <= 12) return "12 Hours";
  if (hours <= 24) return "1 Day";
  if (hours <= 48) return "2 Days";
  if (hours <= 72) return "3 Days";
  if (hours <= 120) return "5 Days";
  if (hours <= 168) return "7 Days";
  return "14 Days";
}

// Get initial participant count from localStorage or URL params
// MUST be defined OUTSIDE component to avoid "Cannot access before initialization" error
function getInitialParticipantCount(participantsParam: string | null): number {
  // First check localStorage for pending session data
  try {
    const pendingSession = localStorage.getItem('shakatah-pending-session');
    if (pendingSession) {
      const { participantCount } = JSON.parse(pendingSession);
      if (participantCount && participantCount > 1) {
        return Math.max(1, participantCount - 1); // -1 because participants array excludes organizer
      }
    }
  } catch (e) {
    console.error("Error reading localStorage:", e);
  }
  // Fallback to URL param
  if (participantsParam) {
    return Math.max(1, Number(participantsParam) - 1);
  }
  return 1; // Default to 1 friend
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function ShakatahSplitSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const clearShakatahCart = useShakatahCartStore((state) => state.clearCart);
  const regularCartItems = useCartStore((state) => state.items);
  const regularCartHasItems = regularCartItems.length > 0;
  
  const sessionToken = searchParams.get('session') || searchParams.get('token');
  const participantsParam = searchParams.get('participants');
  
  const [loading, setLoading] = useState(true);
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const [merchantSettings, setMerchantSettings] = useState<MerchantSettings | null>(null);
  const [step, setStep] = useState<"address" | "participants" | "friends" | "payment" | "complete">("address");
  
  // Address validation state
  const [validatedAddress, setValidatedAddress] = useState<ShippingAddress | null>(null);
  const [validatedPricing, setValidatedPricing] = useState<{ subtotal: number; tax: number; shipping: number; total: number } | null>(null);
  const [shippingWarning, setShippingWarning] = useState<string | null>(null);
  
  // Gift option state
  const [isGift, setIsGift] = useState(false);
  const [organizerGiftMessage, setOrganizerGiftMessage] = useState("");
  
  // Cancellation fee agreement
  const [agreedToCancellationFee, setAgreedToCancellationFee] = useState(false);
  
  // Request ID after creation (for organizer payment)
  const [requestId, setRequestId] = useState<string | null>(null);
  const [organizerParticipantId, setOrganizerParticipantId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Recurring reminder dialog state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  
  // Organizer details (from Shopify checkout)
  const [organizerDetails, setOrganizerDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  
  // Friends/participants for split payment (NOT including organizer)
  const [participants, setParticipants] = useState<Participant[]>(() => {
    const count = getInitialParticipantCount(participantsParam);
    return Array.from({ length: count }, (_, i) => ({
      id: String(i + 1),
      name: "",
      mobile: "",
      emails: [""],
    }));
  });

  // Checkout URL for organizer (Shopify checkout)
  const [organizerCheckoutUrl, setOrganizerCheckoutUrl] = useState<string | null>(null);

  // Fetch pending request and subscribe to realtime updates
  useEffect(() => {
    if (!sessionToken) {
      toast({
        title: "Invalid session",
        description: "No session token found. Please start over from the product page.",
        variant: "destructive",
      });
      return;
    }

    const fetchPendingRequest = async () => {
      setLoading(true);
      
      console.log("Fetching pending request with token:", sessionToken);
      
      const { data, error } = await supabase
        .from("pending_split_requests")
        .select("*")
        .eq("session_token", sessionToken)
        .maybeSingle();

      console.log("Pending request result:", { data, error, sessionToken });

      if (error) {
        console.error("Supabase error fetching pending request:", error);
        toast({
          title: "Error loading session",
          description: `Database error: ${error.message}`,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      
      if (!data) {
        console.error("No data found for session token:", sessionToken);
        toast({
          title: "Session not found",
          description: "Your split payment session has expired or is invalid. Please start over from the product page.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Set the pending request data
      setPendingRequest(data as PendingRequest);

      // Set organizer details from pending request
      if (data.customer_email) {
        setOrganizerDetails(prev => ({ ...prev, email: data.customer_email }));
      }
      
      // Check if address is already set (status is 'ready')
      if (data.status === 'ready' && data.shipping_address) {
        // Skip to participants step if address already validated
        setStep("participants");
        
        // Set validated pricing from stored data
        setValidatedPricing({
          subtotal: data.subtotal || 0,
          tax: data.total_tax || 0,
          shipping: data.total_shipping || 0,
          total: data.total_price || 0,
        });
      }
      
      // Parse shipping address for name
      if (data.shipping_address) {
        try {
          const addr = typeof data.shipping_address === 'string' 
            ? JSON.parse(data.shipping_address) 
            : data.shipping_address;
          if (addr.first_name || addr.name) {
            const parsedFirstName = addr.first_name || addr.name?.split(' ')[0] || '';
            const parsedLastName = addr.last_name || addr.name?.split(' ').slice(1).join(' ') || '';
            setOrganizerDetails(prev => ({
              ...prev,
              firstName: parsedFirstName,
              lastName: parsedLastName,
            }));
          }
          if (addr.phone) {
            setOrganizerDetails(prev => ({ ...prev, phone: addr.phone }));
          }
        } catch (e) {
          console.error("Error parsing shipping address:", e);
        }
      }

      // Set participant count from database if available
      if (data.participant_count && data.participant_count > 1) {
        const friendCount = data.participant_count - 1; // -1 because array excludes organizer
        setParticipants(prev => {
          // Only update if current count doesn't match
          if (prev.length !== friendCount) {
            return Array.from({ length: friendCount }, (_, i) => ({
              id: String(Date.now() + i),
              name: prev[i]?.name || "",
              mobile: prev[i]?.mobile || "",
              emails: prev[i]?.emails || [""],
            }));
          }
          return prev;
        });
      }

      // Fetch merchant settings using public function (bypasses RLS)
      if (data.merchant_id) {
        console.log("Fetching merchant settings for:", data.merchant_id);
        const { data: merchantData, error: merchantError } = await supabase
          .rpc("get_merchant_public_settings", { _merchant_id: data.merchant_id });
        
        console.log("Merchant settings result:", { merchantData, merchantError });
        
        if (merchantData && merchantData.length > 0) {
          const merchant = merchantData[0];
          setMerchantSettings({
            store_name: merchant.store_name,
            store_domain: merchant.store_domain,
            default_deadline_hours: merchant.default_deadline_hours ?? 72,
            cancellation_fee_type: merchant.cancellation_fee_type ?? "fixed",
            cancellation_fee_value: merchant.cancellation_fee_value ?? 100,
            currency: (merchant.currency as Currency) ?? "USD",
          });
        } else {
          console.warn("No merchant settings found, using defaults");
          // Set default values if merchant not found
          setMerchantSettings({
            store_name: "Store",
            store_domain: "",
            default_deadline_hours: 72,
            cancellation_fee_type: "fixed",
            cancellation_fee_value: 100,
            currency: "USD",
          });
        }
      }

      setLoading(false);
    };

    fetchPendingRequest();

    // Subscribe to realtime updates for this pending request
    const channel = supabase
      .channel(`pending-request-${sessionToken}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pending_split_requests',
          filter: `session_token=eq.${sessionToken}`,
        },
        (payload) => {
          console.log("Pending request updated:", payload);
          setPendingRequest(payload.new as unknown as PendingRequest);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionToken, toast]);

  // Use validated pricing if available, otherwise fall back to pending request
  const activePricing = validatedPricing || {
    subtotal: pendingRequest?.subtotal || 0,
    tax: pendingRequest?.total_tax || 0,
    shipping: pendingRequest?.total_shipping || 0,
    total: pendingRequest?.total_price || 0,
  };
  
  // Calculate split amounts
  const totalPrice = activePricing.total;
  const splitAmount = totalPrice / (participants.length + 1); // +1 for organizer
  
  // Handle address validation callback
  const handleAddressValidated = (
    address: ShippingAddress, 
    pricing: { subtotal: number; tax: number; shipping: number; total: number },
    warning?: string | null,
    organizer?: { firstName: string; lastName: string; email: string; phone: string }
  ) => {
    setValidatedAddress(address);
    setValidatedPricing(pricing);
    if (warning) {
      setShippingWarning(warning);
    }
    
    // Update organizer details from the form if provided
    if (organizer) {
      setOrganizerDetails(prev => ({
        ...prev,
        firstName: organizer.firstName,
        lastName: organizer.lastName,
        email: organizer.email,
        phone: organizer.phone,
      }));
    }
    
    // Move to participants step
    setStep("participants");
  };
  
  // Calculate cancellation fee display - uses merchant settings when available
  const getCancellationFeeDisplay = (): string => {
    // Use merchant settings if available, otherwise use 1% default
    const feeType = merchantSettings?.cancellation_fee_type ?? 'percentage';
    const feeValue = merchantSettings?.cancellation_fee_value ?? 1;
    const currency = merchantSettings?.currency ?? "USD";
    
    if (!merchantSettings) return "1%";
    
    if (feeType === 'percentage') {
      const feeAmount = (totalPrice * feeValue) / 100;
      return `${feeValue}% (${formatCurrency(feeAmount, currency)})`;
    }
    return formatCurrency(feeValue, currency);
  };

  // Check for duplicate phone numbers (including organizer)
  const getDuplicatePhones = (): string[] => {
    const allPhones = [
      organizerDetails.phone.trim(),
      ...participants.map(p => p.mobile.trim())
    ].filter(phone => phone !== "");
    
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    
    for (const phone of allPhones) {
      // Normalize phone number for comparison (remove spaces, dashes)
      const normalized = phone.replace(/[\s\-]/g, "");
      if (seen.has(normalized)) {
        duplicates.add(phone);
      }
      seen.add(normalized);
    }
    
    return Array.from(duplicates);
  };
  
  const hasDuplicatePhones = (): boolean => {
    return getDuplicatePhones().length > 0;
  };

  // Check for duplicate emails (including organizer)
  const getDuplicateEmails = (): string[] => {
    const allEmails = [
      organizerDetails.email.trim().toLowerCase(),
      ...participants.flatMap(p => p.emails.map(e => e.trim().toLowerCase()))
    ].filter(email => email !== "");
    
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    
    for (const email of allEmails) {
      if (seen.has(email)) {
        duplicates.add(email);
      }
      seen.add(email);
    }
    
    return Array.from(duplicates);
  };
  
  const hasDuplicateEmails = (): boolean => {
    return getDuplicateEmails().length > 0;
  };

  const isParticipantsValid = () => {
    const allFieldsValid = participants.every(
      (p) =>
        p.name.trim() !== "" &&
        p.emails.length > 0 &&
        p.emails.every((email) => email.trim() !== "" && email.includes("@"))
    );
    
    return allFieldsValid && !hasDuplicatePhones() && !hasDuplicateEmails();
  };

  const isOrganizerValid = () => {
    return (
      organizerDetails.firstName.trim() !== "" &&
      organizerDetails.lastName.trim() !== "" &&
      organizerDetails.email.trim() !== "" &&
      organizerDetails.email.includes("@") &&
      organizerDetails.phone.trim() !== ""
    );
  };

  const addParticipant = () => {
    if (participants.length >= 19) {
      toast({
        title: "Maximum participants reached",
        description: "You can split payment with up to 20 people total",
        variant: "destructive",
      });
      return;
    }
    setParticipants([
      ...participants,
      { id: Date.now().toString(), name: "", mobile: "", emails: [""] },
    ]);
  };

  // Set exact number of participants
  const setParticipantCount = (count: number) => {
    const currentCount = participants.length;
    if (count === currentCount) return;
    
    if (count > currentCount) {
      // Add more participants
      const newParticipants = [...participants];
      for (let i = currentCount; i < count; i++) {
        newParticipants.push({ id: Date.now().toString() + i, name: "", mobile: "", emails: [""] });
      }
      setParticipants(newParticipants);
    } else {
      // Remove participants from the end
      setParticipants(participants.slice(0, count));
    }
  };

  const removeParticipant = (id: string) => {
    if (participants.length <= 1) {
      toast({
        title: "Minimum participants required",
        description: "You need at least 1 friend to split the payment",
        variant: "destructive",
      });
      return;
    }
    setParticipants(participants.filter((p) => p.id !== id));
  };

  const updateParticipant = (id: string, field: "name" | "mobile", value: string) => {
    setParticipants(
      participants.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const updateParticipantEmail = (participantId: string, emailIndex: number, value: string) => {
    setParticipants(
      participants.map((p) => {
        if (p.id === participantId) {
          const newEmails = [...p.emails];
          newEmails[emailIndex] = value;
          return { ...p, emails: newEmails };
        }
        return p;
      })
    );
  };

  const addEmailToParticipant = (participantId: string) => {
    setParticipants(
      participants.map((p) => {
        if (p.id === participantId && p.emails.length < 5) {
          return { ...p, emails: [...p.emails, ""] };
        }
        return p;
      })
    );
  };

  const removeEmailFromParticipant = (participantId: string, emailIndex: number) => {
    setParticipants(
      participants.map((p) => {
        if (p.id === participantId && p.emails.length > 1) {
          const newEmails = p.emails.filter((_, i) => i !== emailIndex);
          return { ...p, emails: newEmails };
        }
        return p;
      })
    );
  };

  // Step 1: Create the split payment request
  const handleCreateRequest = async () => {
    if (!pendingRequest) return;
    
    if (!isOrganizerValid()) {
      toast({
        title: "Incomplete details",
        description: "Please fill in your name and phone number",
        variant: "destructive",
      });
      return;
    }
    
    if (!isParticipantsValid()) {
      toast({
        title: "Incomplete details",
        description: "Please fill in all participant details correctly",
        variant: "destructive",
      });
      return;
    }

    if (!agreedToCancellationFee) {
      toast({
        title: "Agreement required",
        description: "Please agree to the cancellation fee terms to proceed",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    
    try {
      // Flatten participants with multiple emails
      const flattenedParticipants = participants.flatMap(p => 
        p.emails.map(email => ({
          name: p.name,
          email: email,
          mobile: p.mobile,
        }))
      );

      // Get store domain from merchant using RPC to bypass RLS
      let storeDomain = '';
      if (pendingRequest.merchant_id) {
        const { data: merchantSettings } = await supabase
          .rpc('get_merchant_public_settings', { _merchant_id: pendingRequest.merchant_id });
        
        if (merchantSettings && merchantSettings.length > 0) {
          storeDomain = merchantSettings[0].store_domain || '';
        }
      }

      const { data, error } = await supabase.functions.invoke('send-split-payment', {
        body: {
          productName: pendingRequest.product_title,
          totalPrice: pendingRequest.total_price,
          splitAmount,
          participants: flattenedParticipants,
          customerName: `${organizerDetails.firstName} ${organizerDetails.lastName}`.trim(),
          customerEmail: organizerDetails.email,
          customerPhone: organizerDetails.phone,
          isGift,
          organizerGiftMessage: isGift ? organizerGiftMessage : null,
          organizerMessage: organizerDetails.message || null,
          productImageUrl: pendingRequest.product_image,
          productDescription: null,
          variantId: pendingRequest.variant_id,
          storeDomain,
          shopifyDraftOrderId: pendingRequest.shopify_draft_order_id,
          shippingAddress: pendingRequest.shipping_address,
          taxAmount: pendingRequest.total_tax,
          shippingAmount: pendingRequest.total_shipping,
          organizerPaysFirst: true,
          merchantId: pendingRequest.merchant_id, // Pass merchant ID directly
        },
      });

      if (error) throw error;

      // Split was successfully created - clear the cart immediately
      // CRITICAL: Set markers BEFORE clearing cart to block any writes during state change
      localStorage.removeItem('shakatah-pending-session');
      localStorage.setItem('shakatah-split-completed', 'true');
      localStorage.setItem('shakatah-checkout-in-progress', 'true');
      localStorage.setItem('shakatah-checkout-timestamp', String(Date.now())); // Timestamp for expiration
      sessionStorage.setItem('shakatah-checkout-completed-this-session', 'true');
      
      // Also clear localStorage cart BEFORE Zustand state change
      // This prevents persist middleware from reading/writing stale data
      try {
        localStorage.removeItem('shakatah-split-cart');
        // Clear widget cart keys too
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('shakatah-split-cart-')) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        console.error('Error clearing cart localStorage:', e);
      }
      
      // NOW clear Zustand state (markers are already set, so writes will be blocked)
      clearShakatahCart();

      setRequestId(data.requestId);
      setOrganizerParticipantId(data.organizerParticipantId);
      setStep("payment");
      
      toast({
        title: "Ready for payment",
        description: "Complete your payment to send invitations to friends",
      });

    } catch (error: any) {
      console.error("Error creating request:", error);
      toast({
        title: "Failed to create split request",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Handle organizer payment - creates Shopify checkout and redirects
  const handleOrganizerPayment = async () => {
    if (!requestId || !organizerParticipantId) {
      toast({
        title: "Error",
        description: "Payment session not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Create Shopify checkout for organizer's share
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-participant-checkout', {
        body: {
          requestId: requestId,
          participantId: organizerParticipantId,
        },
      });

      if (checkoutError) throw checkoutError;

      if (!checkoutData?.checkoutUrl) {
        throw new Error("Failed to create checkout - no URL returned");
      }

      // Store the checkout URL
      setOrganizerCheckoutUrl(checkoutData.checkoutUrl);

      // Clean up ALL session and cart storage
      localStorage.removeItem('shakatah_session');
      localStorage.removeItem('shakatah-pending-session');
      
      // CRITICAL: Set checkout markers BEFORE clearing cart
      // These markers tell the system to block rehydration on next page load
      localStorage.setItem('shakatah-checkout-in-progress', 'true');
      localStorage.setItem('shakatah-split-completed', 'true');
      localStorage.setItem('shakatah-checkout-timestamp', String(Date.now())); // Timestamp for expiration
      
      // ALSO set sessionStorage flag for same-session protection
      // This survives page refreshes within the same browser session
      sessionStorage.setItem('shakatah-checkout-completed-this-session', 'true');
      
      // Clear the session storage hash to prevent stale cart imports
      sessionStorage.removeItem('shakatah-imported-cart-hash');
      
      // Clear ALL cart-related localStorage keys
      try {
        // Main cart storage
        localStorage.removeItem('shakatah-split-cart');
        
        // Also clear any widget-specific cart keys (format: shakatah-split-cart-[domain])
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('shakatah-split-cart-')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          console.log('Clearing widget cart key:', key);
          localStorage.removeItem(key);
        });
        
        console.log('All shakatah cart localStorage cleared before checkout');
      } catch (e) {
        console.error('Failed to clear shakatah cart from localStorage:', e);
      }
      
      // Clear the Zustand state
      clearShakatahCart();

      toast({
        title: "Checkout ready!",
        description: "Opening merchant checkout page...",
      });

      // Redirect to Shopify checkout in new tab
      window.open(checkoutData.checkoutUrl, '_blank');

      // Show completion state (payment will be verified via webhook)
      setStep("complete");

    } catch (error: any) {
      console.error("Error creating checkout:", error);
      toast({
        title: "Checkout failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
          <h1 className="text-2xl font-bold">Loading your order...</h1>
          <p className="text-muted-foreground">
            Please wait while we retrieve your order details
          </p>
        </div>
      </div>
    );
  }

  // Note: We no longer wait for Shopify address - we go directly to split setup
  // The "awaiting_address" status is now just for internal tracking

  // No request found
  if (!pendingRequest) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Session Not Found</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your split payment session has expired or is invalid. 
              Please start over from the product page.
            </p>
            <Button 
              onClick={() => {
                if (merchantSettings?.store_domain) {
                  window.location.href = `https://${merchantSettings.store_domain}`;
                } else {
                  navigate("/");
                }
              }} 
              className="w-full"
            >
              Back to Store
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state - waiting for organizer payment
  if (step === "complete") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <CreditCard className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl">Complete Your Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-muted-foreground">
              Complete your payment for your share ({formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}) on the merchant's checkout page. 
              Once confirmed, invitations will be sent to your friends automatically.
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{pendingRequest.product_title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{participants.length + 1} people splitting {formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Your share: <strong>{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</strong></span>
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-warning-foreground">Important</p>
                  <p className="text-muted-foreground">
                    Your friends will only receive their payment links after you complete your payment.
                  </p>
                </div>
              </div>
            </div>

            {organizerCheckoutUrl && (
              <Button 
                onClick={() => window.open(organizerCheckoutUrl, '_blank')}
                className="w-full"
                variant="default"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Pay Your Share ({formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")})
              </Button>
            )}

            {requestId && (
              <Button 
                onClick={() => navigate(`/split/${requestId}`)} 
                className="w-full"
                variant="outline"
              >
                Track Payment Status
              </Button>
            )}

            {/* Post-checkout options: Regular cart or continue shopping */}
            <div className="pt-4 border-t border-border space-y-3">
              {regularCartHasItems ? (
                <div className="bg-secondary/50 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-center text-muted-foreground">
                    You still have items in your regular cart
                  </p>
                  <Button 
                    onClick={() => navigate("/")}
                    variant="secondary"
                    className="w-full"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Go to Regular Cart ({regularCartItems.length} item{regularCartItems.length !== 1 ? 's' : ''})
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => {
                    if (merchantSettings?.store_domain) {
                      window.location.href = `https://${merchantSettings.store_domain}`;
                    } else {
                      navigate("/");
                    }
                  }}
                  variant="ghost"
                  className="w-full"
                >
                  Continue Shopping
                </Button>
              )}
            </div>

            {/* Recurring Reminder Prompt */}
            {isGift && (
              <div className="pt-4 border-t border-border">
                <div className="bg-primary/5 rounded-lg p-4 text-center space-y-3">
                  <Gift className="h-8 w-8 text-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    Is this a recurring occasion like a birthday or anniversary?
                  </p>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReminderDialog(true)}
                  >
                    🔔 Set a Reminder for Next Year
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recurring Reminder Dialog */}
        <RecurringReminderDialog
          open={showReminderDialog}
          onOpenChange={setShowReminderDialog}
          requestId={requestId || undefined}
          recipientName={`${organizerDetails.firstName} ${organizerDetails.lastName}`.trim()}
          productName={pendingRequest?.product_title}
        />
      </div>
    );
  }

  // Parse shipping address for display
  const getShippingAddressDisplay = () => {
    if (!pendingRequest?.shipping_address) return null;
    try {
      const addr = typeof pendingRequest.shipping_address === 'string' 
        ? JSON.parse(pendingRequest.shipping_address) 
        : pendingRequest.shipping_address;
      return {
        line1: addr.address1,
        line2: addr.address2,
        city: addr.city,
        province: addr.province,
        zip: addr.zip,
        country: addr.country,
      };
    } catch (e) {
      return null;
    }
  };

  const shippingAddress = getShippingAddressDisplay();

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              if (step === "address") {
                navigate(-1);
              } else if (step === "participants") {
                setStep("address");
              } else if (step === "friends") {
                setStep("participants");
              } else if (step === "payment") {
                setStep("friends");
              }
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Split Payment</h1>
            <p className="text-muted-foreground">
              {step === "address" && "Step 1: Enter delivery address"}
              {step === "participants" && "Step 2: Choose number of people"}
              {step === "friends" && "Step 3: Add friend details"}
              {step === "payment" && "Step 4: Complete payment"}
            </p>
          </div>
        </div>

        {/* Progress indicator - 4 steps now */}
        <div className="flex gap-2">
          <div className={`flex-1 h-2 rounded-full ${["address", "participants", "friends", "payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex-1 h-2 rounded-full ${["participants", "friends", "payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex-1 h-2 rounded-full ${["friends", "payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex-1 h-2 rounded-full ${["payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
        </div>

        {/* Order Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              {pendingRequest.product_image && (
                <img 
                  src={pendingRequest.product_image} 
                  alt={pendingRequest.product_title}
                  className="w-20 h-20 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">{pendingRequest.product_title}</h3>
                <p className="text-sm text-muted-foreground">Qty: {pendingRequest.quantity}</p>
              </div>
            </div>

            {/* Pricing breakdown - only show if address validated */}
            {step !== "address" && (
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(activePricing.subtotal, merchantSettings?.currency ?? "USD")}</span>
                </div>
                {activePricing.shipping > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Truck className="h-3 w-3" /> Shipping
                    </span>
                    <span>{formatCurrency(activePricing.shipping, merchantSettings?.currency ?? "USD")}</span>
                  </div>
                )}
                {activePricing.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>{formatCurrency(activePricing.tax, merchantSettings?.currency ?? "USD")}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}</span>
                </div>
                
                {/* Shipping warning if using fallback rate */}
                {shippingWarning && (
                  <p className="text-xs text-warning mt-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {shippingWarning}
                  </p>
                )}
              </div>
            )}
            
            {/* Show estimated total on address step */}
            {step === "address" && (
              <div className="border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(pendingRequest.subtotal, merchantSettings?.currency ?? "USD")}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  * Shipping and taxes will be calculated after address validation
                </p>
              </div>
            )}

            {/* Shipping address - only show after validation */}
            {step !== "address" && shippingAddress && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Shipping to:</p>
                    <p className="text-muted-foreground">
                      {shippingAddress.line1}
                      {shippingAddress.line2 && `, ${shippingAddress.line2}`}
                      <br />
                      {shippingAddress.city}, {shippingAddress.province} {shippingAddress.zip}
                      <br />
                      {shippingAddress.country}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Address Step - Collect delivery address and validate with Shopify */}
        {step === "address" && sessionToken && (
          <>
            {/* Split Payment Info Banner */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6 pb-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-foreground">You're setting up a Split Payment</h3>
                    <p className="text-sm text-muted-foreground">
                      After entering the delivery address, you'll choose how many friends to split with and invite them to pay their share.
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>Step 1: Address</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        <span>Step 2: Choose split</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <span>Step 3: Invite friends</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CreditCard className="h-3.5 w-3.5" />
                        <span>Step 4: Pay your share</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <ShippingAddressForm
              sessionToken={sessionToken}
              onAddressValidated={handleAddressValidated}
              initialEmail={organizerDetails.email}
            />
          </>
        )}

        {/* Participants Step - Select number of people and see split amount */}
        {step === "participants" && (
          <>
            {/* Split Amount Display */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Each person pays</p>
                  <p className="text-3xl font-bold text-primary">{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Split between {participants.length + 1} people
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Participant Count Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  How many people are splitting?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Select the total number of people (including yourself) who will share the payment.
                </p>
                
                <Select 
                  value={String(participants.length + 1)} 
                  onValueChange={(val) => setParticipantCount(parseInt(val) - 1)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select number of people" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 19 }, (_, i) => i + 2).map((num) => (
                      <SelectItem key={num} value={String(num)}>
                        {num} people {num === 2 ? "(you + 1 friend)" : `(you + ${num - 1} friends)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Payment Terms */}
                {merchantSettings && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <p className="text-sm font-medium">Payment Terms</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Deadline</p>
                          <p className="font-medium">{getDeadlineLabel(merchantSettings.default_deadline_hours)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Cancellation Fee</p>
                          <p className="font-medium">{getCancellationFeeDisplay()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Continue Button */}
            <Button
              className="w-full"
              size="lg"
              onClick={() => setStep("friends")}
            >
              Continue to Add Friends
              <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
            </Button>
          </>
        )}

        {/* Split Amount Display - shown on friends step */}
        {step === "friends" && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Each person pays</p>
                <p className="text-3xl font-bold text-primary">{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Split between {participants.length + 1} people
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "friends" && (
          <>
            {/* Your Details Summary (read-only, from address step) */}
            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  Your Details
                  <Badge variant="secondary" className="ml-auto text-xs">From delivery step</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>{" "}
                    <span className="font-medium">{organizerDetails.firstName && organizerDetails.lastName ? `${organizerDetails.firstName} ${organizerDetails.lastName}` : "Not set"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>{" "}
                    <span className="font-medium">{organizerDetails.email || "Not set"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>{" "}
                    <span className="font-medium">{organizerDetails.phone || "Not set"}</span>
                  </div>
                </div>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto mt-2 text-xs"
                  onClick={() => setStep("address")}
                >
                  Edit your details
                </Button>
              </CardContent>
            </Card>

            {/* Organizer Message */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Message to Friends (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add a personal note to your friends, e.g., 'Hey everyone, let's chip in for Sarah's birthday gift!'"
                  value={organizerDetails.message || ""}
                  onChange={(e) => {
                    if (e.target.value.length <= 250) {
                      setOrganizerDetails({...organizerDetails, message: e.target.value});
                    }
                  }}
                  className="resize-none"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground text-right mt-2">
                  {organizerDetails.message?.length || 0}/250 characters
                </p>
              </CardContent>
            </Card>

            {/* Participants */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Friends to Split With ({participants.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {participants.map((participant, index) => (
                  <div key={participant.id} className="space-y-3 p-4 bg-muted/30 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">Friend {index + 1}</Badge>
                      {participants.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeParticipant(participant.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid gap-3">
                      <Input
                        placeholder="Name *"
                        value={participant.name}
                        onChange={(e) => updateParticipant(participant.id, "name", e.target.value)}
                      />
                      <Input
                        placeholder="Phone Number (optional)"
                        value={participant.mobile}
                        onChange={(e) => updateParticipant(participant.id, "mobile", e.target.value)}
                      />
                      
                      {participant.emails.map((email, emailIndex) => (
                        <div key={emailIndex} className="flex gap-2">
                          <Input
                            placeholder="Email *"
                            type="email"
                            value={email}
                            onChange={(e) => updateParticipantEmail(participant.id, emailIndex, e.target.value)}
                          />
                          {participant.emails.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeEmailFromParticipant(participant.id, emailIndex)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      
                      {participant.emails.length < 5 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-fit"
                          onClick={() => addEmailToParticipant(participant.id)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add another email
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Gift Option */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Gift className="h-5 w-5 text-primary" />
                    <div>
                      <Label htmlFor="gift-switch" className="font-medium">Is this a gift?</Label>
                      <p className="text-sm text-muted-foreground">Add a personal message</p>
                    </div>
                  </div>
                  <Switch
                    id="gift-switch"
                    checked={isGift}
                    onCheckedChange={setIsGift}
                  />
                </div>
                
                {isGift && (
                  <div className="mt-4">
                    <Textarea
                      placeholder="Write your gift message..."
                      value={organizerGiftMessage}
                      onChange={(e) => setOrganizerGiftMessage(e.target.value)}
                      className="min-h-[80px]"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cancellation Fee Agreement */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cancellation-agreement"
                    checked={agreedToCancellationFee}
                    onCheckedChange={(checked) => setAgreedToCancellationFee(checked === true)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="cancellation-agreement" className="font-medium cursor-pointer">
                      I understand and agree to the cancellation terms
                    </Label>
                    <div className="text-sm text-muted-foreground space-y-1.5">
                      <p>
                        If all participants don't complete payment within{" "}
                        <strong>{getDeadlineLabel(merchantSettings?.default_deadline_hours || 72)}</strong>,
                        the order will be cancelled:
                      </p>
                      <ul className="list-disc list-inside pl-1 space-y-1">
                        <li>
                          A cancellation fee of <strong>{getCancellationFeeDisplay()}</strong> will be deducted from <strong>your (organizer's) payment</strong>
                        </li>
                        <li>
                          All other participants will receive a <strong>full refund</strong> to their original payment method
                        </li>
                        <li>
                          Refunds are typically processed within 5-10 business days
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation Status - Debug helper */}
            {(!isOrganizerValid() || !isParticipantsValid() || !agreedToCancellationFee) && (
              <Card className="border-destructive/50 bg-destructive/5">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-destructive mb-2">Please complete the following:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {!organizerDetails.firstName.trim() && <li>• Enter your first name</li>}
                    {!organizerDetails.lastName.trim() && <li>• Enter your last name</li>}
                    {!organizerDetails.email.trim() && <li>• Enter your email</li>}
                    {organizerDetails.email.trim() && !organizerDetails.email.includes("@") && <li>• Enter a valid email</li>}
                    {!organizerDetails.phone.trim() && <li>• Enter your phone number</li>}
                    {participants.some(p => !p.name.trim() || p.emails.length === 0 || p.emails.some(e => !e.trim() || !e.includes("@"))) && (
                      <li>• Complete all friend details (name and valid email)</li>
                    )}
                    {hasDuplicatePhones() && (
                      <li className="text-destructive font-medium">• Each person must have a unique phone number - duplicates found</li>
                    )}
                    {hasDuplicateEmails() && (
                      <li className="text-destructive font-medium">• Each person must have a unique email address - duplicates found</li>
                    )}
                    {!agreedToCancellationFee && <li>• Agree to the cancellation terms</li>}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Continue Button */}
            <Button
              className="w-full"
              size="lg"
              onClick={handleCreateRequest}
              disabled={isSending || !isOrganizerValid() || !isParticipantsValid() || !agreedToCancellationFee}
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up split payment...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Continue to Payment
                </>
              )}
            </Button>
          </>
        )}

        {step === "payment" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                You're One Step Away!
              </CardTitle>
              <CardDescription>
                Complete your payment to send invitations to your friends
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-2">Your share amount</p>
                <p className="text-4xl font-bold text-primary">{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</p>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  <strong>How it works:</strong> You will be redirected to the merchant's checkout to complete your payment. 
                  Once paid, your friends will automatically receive email invitations with their secure payment links.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">Payment invitations will be sent to:</p>
                <ul className="space-y-1">
                  {participants.map((p) => (
                    <li key={p.id} className="text-sm flex items-center gap-2">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      {p.name} ({p.emails[0]})
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                className="w-full"
                size="lg"
                onClick={handleOrganizerPayment}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating checkout...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay {formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")} via Merchant Checkout
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ShakatahSplitSetup;
